import { mainAppUrl } from '../utils/appOrigin';

export interface PlanActivationParams {
  userName: string;
  planName: string;
  amount: number;
  currency: string;
  billingPeriod: 'monthly' | 'annual';
  startDate: Date;
  endDate: Date;
  invoiceDownloadUrl?: string;
}

class SystemEmailService {
  private static readonly BRAND_COLOR = '#6366f1';
  private static readonly GRADIENT = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
  private static readonly FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

  private static getBaseLayout(title: string, content: string, preheader: string = ''): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title}</title>
  
        <style>
          body {
            margin:0;
            padding:60px 24px;
            background:#ffffff;
            color:#000000;
            font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
            font-size:18px;
            line-height:1.7;
          }
  
          .wrapper {
            max-width:620px;
            margin:auto;
          }
  
          .logo {
            font-size:32px;
            font-weight:600;
            margin-bottom:40px;
          }
  
          .content p {
            margin:0 0 28px 0;
          }
  
          .button {
            display:inline-block;
            background:${this.BRAND_COLOR};
            color:white !important;
            padding:14px 22px;
            border-radius:6px;
            text-decoration:none;
            font-weight:600;
            margin:20px 0;
          }
  
          .passcode {
            font-size:56px;
            font-weight:700;
            letter-spacing:4px;
            margin:20px 0 36px 0;
          }
  
          .notice {
            font-size:16px;
            margin-top:28px;
          }
  
          .footer {
            margin-top:40px;
            font-size:16px;
          }
  
          .preheader {
            display:none;
            max-height:0;
            overflow:hidden;
          }
  
        </style>
      </head>
  
      <body>
  
        <span class="preheader">${preheader}</span>
  
        <div class="wrapper">
  
          <div class="logo">
            Scubamail
          </div>
  
          <div class="content">
            ${content}
          </div>
  
          <div class="footer">
            Thank you,<br>
            Scubamail
          </div>
  
        </div>
  
      </body>
      </html>
    `;
  }

  static getVerificationEmail(userName: string, link: string): string {
    const content = `
      <p>Hi ${userName},</p>
      <p>Welcome to Scubamail! We're excited to have you on board. To complete your registration and unlock all features, please verify your email address:</p>
      <div>
        <a href="${link}" class="button">Verify Email Address</a>
      </div>
      <p style="font-size: 14px; color: #666; margin-top: 20px;">Or copy and paste this link in your browser:<br>
      <a href="${link}" style="color: #666; word-break: break-all;">${link}</a></p>
      <div class="notice">
        <strong>Security Check:</strong> This link will expire in 24 hours. If you didn't create this account, you can safely ignore this email.
      </div>
    `;
    return this.getBaseLayout('Verify Your Email', content, 'Complete your Scubamail registration');
  }

  static getPasswordResetEmail(userName: string, link: string): string {
    const content = `
      <p>Hi ${userName},</p>
      <p>We received a request to reset the password for your Scubamail account. Click the button below to choose a new password:</p>
      <div>
        <a href="${link}" class="button">Reset Password</a>
      </div>
      <p style="font-size: 14px; color: #666; margin-top: 20px;">If the button doesn't work, copy and paste this link:<br>
      <a href="${link}" style="color: #666; word-break: break-all;">${link}</a></p>
      <div class="notice">
        <strong>Security Notice:</strong> This link expires in 1 hour. If you didn't request a password reset, your account is safe and no action is required.
      </div>
    `;
    return this.getBaseLayout('Reset Your Password', content, 'Password reset request for your Scubamail account');
  }

  static getPasscodeEmail(userName: string, passcode: string, type: 'email_verification' | 'password_reset' | 'sender_verification'): string {
    const isReset = type === 'password_reset';
    const isSender = type === 'sender_verification';
    const title = isReset ? 'Reset Your Password' : isSender ? 'Verify Sender Email' : 'Verify Your Email';

    const instruction = isReset 
      ? 'To reset your password, please use the following secure code in the app:' 
      : isSender 
        ? 'To verify this email address for sending campaigns, please use the following verification code in the app:' 
        : 'To complete your registration, please enter the following verification code in the app:';

    const content = `
      <p>Hi ${userName},</p>
      <p>${instruction}</p>
      <div class="passcode">${passcode}</div>
      <div class="notice">
        <strong>Important:</strong>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>This code will expire in 10 minutes</li>
          <li>Never share this code with anyone</li>
          <li>If you didn't request this, please ignore this email</li>
        </ul>
      </div>
    `;
    return this.getBaseLayout(title, content, `${isReset ? 'Your password reset code' : isSender ? 'Your sender verification code' : 'Your email verification code'} is ${passcode}`);
  }

  static getPlanActivationEmail(params: PlanActivationParams): string {
    const {
      userName,
      planName,
      amount,
      currency,
      billingPeriod,
      startDate,
      endDate,
      invoiceDownloadUrl,
    } = params;

    const formattedStart = startDate.toLocaleDateString();
    const formattedEnd = endDate.toLocaleDateString();
    const amountText = `${amount.toFixed(2)} ${currency.toUpperCase()}`;
    const periodLabel = billingPeriod === 'annual' ? 'Yearly' : 'Monthly';

    const content = `
      <p>Hi ${userName || 'there'},</p>
      <p>Your <strong>${planName}</strong> plan is now active! Here are your subscription details:</p>
      
      <ul style="list-style: none; padding: 0; margin: 24px 0; border-top: 1px solid #eee; border-bottom: 1px solid #eee;">
        <li style="padding: 12px 0; border-bottom: 1px solid #eee;">
          <strong style="display: inline-block; width: 120px;">Plan:</strong> ${planName}
        </li>
        <li style="padding: 12px 0; border-bottom: 1px solid #eee;">
          <strong style="display: inline-block; width: 120px;">Billing:</strong> ${periodLabel}
        </li>
        <li style="padding: 12px 0; border-bottom: 1px solid #eee;">
          <strong style="display: inline-block; width: 120px;">Amount:</strong> ${amountText}
        </li>
        <li style="padding: 12px 0;">
          <strong style="display: inline-block; width: 120px;">Period:</strong> ${formattedStart} — ${formattedEnd}
        </li>
      </ul>

      <div>
        <a href="${invoiceDownloadUrl || mainAppUrl('/settings/billing')}" class="button">
          ${invoiceDownloadUrl ? 'Download Invoice (PDF)' : 'View Invoice'}
        </a>
      </div>

      <p style="font-size: 16px; color: #666; margin-top: 24px;">
        You can manage your subscription anytime from the Billing section in your Scubamail workspace.
      </p>
    `;
    return this.getBaseLayout(`${planName} Plan Active`, content, `Your Scubamail ${planName} plan is now active`);
  }
}

export default SystemEmailService;
